import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WarningCircle, FilePdf, DownloadSimple, CheckCircle } from "@phosphor-icons/react";

export default function Tax1099() {
  const nav = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);

  const load = (y) => api.get(`/tax/1099/${y}`).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(year); }, [year]);

  const dlRep = (repId) => window.open(`${API}/tax/1099/${year}/rep/${repId}/pdf`, "_blank");
  const dlBatch = () => window.open(`${API}/tax/1099/${year}/batch`, "_blank");

  if (!data) return <div className="font-mono text-sm text-muted-foreground">Loading…</div>;

  return (
    <div>
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Year-End 1099-NEC</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Cash-basis payouts ≥ {money(data.threshold)} · records for your accountant · this tool does not e-file
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1.5">
            <Label className="overline">Tax Year</Label>
            <Input data-testid="tax-year" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-28 font-mono" />
          </div>
          <Button data-testid="download-batch-btn" className="gap-2" onClick={dlBatch} disabled={data.eligible_with_w9 === 0}>
            <DownloadSimple size={16} /> Download batch ZIP
          </Button>
        </div>
      </div>

      <div className="mb-6 rounded-sm border border-border bg-card p-4" data-testid="payer-block">
        <div className="overline mb-1">Payer</div>
        <div className="text-sm font-medium">{data.payer.legal_name}{data.payer.dba ? ` DBA ${data.payer.dba}` : ""}</div>
        <div className="font-mono text-xs text-muted-foreground">EIN {data.payer.ein} · {data.payer.address}</div>
      </div>

      <div className="mb-6 grid grid-cols-3 gap-4">
        <div className="rounded-sm border border-border bg-card p-4"><div className="overline">Forms to issue</div><div data-testid="tax-count" className="mt-1 font-mono text-2xl font-semibold">{data.count}</div></div>
        <div className="rounded-sm border border-border bg-card p-4"><div className="overline">Total reported</div><div data-testid="tax-total" className="mt-1 font-mono text-2xl font-semibold text-primary">{money(data.total)}</div></div>
        <div className="rounded-sm border border-border bg-card p-4"><div className="overline">Missing W-9</div><div className="mt-1 font-mono text-2xl font-semibold text-[hsl(var(--warning))]">{data.missing_w9.length}</div></div>
      </div>

      {data.missing_w9.length > 0 && (
        <div data-testid="missing-w9-warning" className="mb-6 rounded-sm border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-[hsl(var(--warning))]">
            <WarningCircle size={18} weight="fill" /> {data.missing_w9.length} rep(s) at/above threshold have NO W-9 — chase before January
          </div>
          <div className="space-y-1">
            {data.missing_w9.map((m) => (
              <div key={m.rep_id} className="flex items-center justify-between text-sm">
                <button className="text-primary hover:underline" onClick={() => nav(`/reps/${m.rep_id}`)}>{m.rep_name}</button>
                <span className="font-mono text-muted-foreground">{money(m.box1)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-sm border border-border">
        <table className="w-full text-sm" data-testid="tax-forms-table">
          <thead className="bg-card"><tr className="border-b border-border">
            {["Recipient", "TIN", "Box 1 (Nonemployee Comp.)", "W-9", ""].map((c, i) => (
              <th key={i} className={`px-4 py-3 overline ${i === 2 ? "text-right" : "text-left"}`}>{c}</th>
            ))}
          </tr></thead>
          <tbody>
            {data.forms.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-16 text-center text-sm text-muted-foreground">No rep reached {money(data.threshold)} in paid commissions for {year}.</td></tr>
            )}
            {data.forms.map((f) => (
              <tr key={f.rep_id} data-testid={`tax-row-${f.rep_id}`} className="border-b border-border hover:bg-accent transition-colors duration-200">
                <td className="px-4 py-2.5 font-medium">{f.rep_name}</td>
                <td className="px-4 py-2.5 font-mono text-muted-foreground">{f.w9_on_file ? `${f.tin_type} ••••${f.tin_last4}` : "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono font-medium">{money(f.box1)}</td>
                <td className="px-4 py-2.5">
                  {f.w9_on_file
                    ? <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--success))]"><CheckCircle size={14} weight="fill" /> on file</span>
                    : <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--warning))]"><WarningCircle size={14} weight="fill" /> missing</span>}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {f.w9_on_file
                    ? <Button data-testid={`tax-pdf-${f.rep_id}`} size="sm" variant="outline" className="gap-1" onClick={() => dlRep(f.rep_id)}><FilePdf size={14} /> 1099-NEC</Button>
                    : <button className="text-xs text-primary hover:underline" onClick={() => nav(`/reps/${f.rep_id}`)}>Add W-9</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
